<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Capacitor } from '@capacitor/core'
import { api } from '@/services/api'
import { getEnvValue } from '@/utils/platform'
import { resolveApkUrl } from '@/services/appUpdate'

const DISMISS_KEY = 'dhikr_smart_banner_dismissed_at'
const SESSION_SHOWN_KEY = 'dhikr_smart_banner_session_shown'
const DISMISS_DAYS = 7
const DEEP_LINK_TIMEOUT_MS = 2200
/** If we hid the banner thinking the app opened, restore it if the user returns within this window */
const RETURN_GRACE_MS = 4000
const ANDROID_PACKAGE = 'com.dhikr.dhikrsession'
/** Approximate reserved height to limit CLS when the fixed banner appears */
const BANNER_PAD_PX = 168

const route = useRoute()
const { t, locale } = useI18n()

const visible = ref(false)
const installUrl = ref('')
const installIsStore = ref(false)
const apkVersion = ref('')
const opening = ref(false)
const showInstallFallback = ref(false)

let deepLinkTimer: ReturnType<typeof setTimeout> | null = null
let deepLinkAttemptActive = false
let leftDuringAttempt = false
let hidAssumingAppOpenAt: number | null = null

const isMobileBrowser = computed(() => {
  if (Capacitor.isNativePlatform()) return false
  if (typeof navigator === 'undefined') return false
  // Skip known crawlers / bots for SEO
  const ua = navigator.userAgent || ''
  if (/bot|crawl|spider|slurp|facebookexternalhit|preview/i.test(ua)) return false
  return /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)
})

const isAndroid = computed(() => {
  if (typeof navigator === 'undefined') return false
  return /Android/i.test(navigator.userAgent || '')
})

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return false
    const at = Number(raw)
    if (!Number.isFinite(at)) return false
    const ms = DISMISS_DAYS * 24 * 60 * 60 * 1000
    return Date.now() - at < ms
  } catch {
    return false
  }
}

function wasShownThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_SHOWN_KEY) === '1'
  } catch {
    return false
  }
}

function markShownThisSession() {
  try {
    sessionStorage.setItem(SESSION_SHOWN_KEY, '1')
  } catch {
    // ignore
  }
}

function setBodyPad(on: boolean) {
  if (typeof document === 'undefined') return
  document.body.style.paddingBottom = on ? `${BANNER_PAD_PX}px` : ''
}

function dismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
  } catch {
    // ignore
  }
  visible.value = false
  opening.value = false
  deepLinkAttemptActive = false
  leftDuringAttempt = false
  hidAssumingAppOpenAt = null
  clearDeepLinkTimer()
}

function clearDeepLinkTimer() {
  if (deepLinkTimer) {
    clearTimeout(deepLinkTimer)
    deepLinkTimer = null
  }
}

function pathForDeepLink(): string {
  const path = `${route.fullPath || '/'}`
  return path.replace(/^\//, '')
}

/**
 * Open the native app without treating a failed attempt as dismiss.
 * Avoid same-origin https navigation (reloads SPA + session flag hides banner).
 */
function tryOpenNativeApp() {
  const pathNoSlash = pathForDeepLink()
  const customUrl = `dhikrsession://${pathNoSlash}`

  if (isAndroid.value) {
    // Intent URL without S.browser_fallback_url so a missing app keeps the user on this page
    window.location.href =
      `intent://${pathNoSlash}#Intent;scheme=dhikrsession;package=${ANDROID_PACKAGE};end`
    return
  }

  // iOS / other: custom scheme (iframe first, then top-level). Do not navigate to https App Link
  // on the same host — that just reloads the web app when the native app is missing.
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = customUrl
  document.body.appendChild(iframe)
  window.setTimeout(() => iframe.remove(), 1500)

  try {
    window.location.href = customUrl
  } catch {
    // ignore
  }
}

async function loadInstallInfo() {
  const envStore = getEnvValue('VITE_PLAY_STORE_URL')
  const envApk = getEnvValue('VITE_APK_DOWNLOAD_URL')

  if (envStore) {
    installUrl.value = envStore
    installIsStore.value = true
  } else if (envApk) {
    installUrl.value = envApk
    installIsStore.value = false
  }

  try {
    const { data } = await api.getAppDownloads()
    if (data.success && data.data) {
      apkVersion.value = data.data.apkVersion || ''
      const playUrl = (data.data.googlePlayUrl || '').trim()
      if (!installIsStore.value && playUrl) {
        installUrl.value = playUrl
        installIsStore.value = true
      }
      if (!installUrl.value && data.data.apkDownloadUrl) {
        installUrl.value = resolveApkUrl(data.data.apkDownloadUrl)
        installIsStore.value = false
      }
    }
  } catch {
    // keep env values if any
  }

  if (!installUrl.value) {
    installUrl.value = resolveApkUrl('/api/v1/public/download/apk')
    installIsStore.value = false
  }
}

function revealInstallFallback() {
  opening.value = false
  deepLinkAttemptActive = false
  leftDuringAttempt = false
  hidAssumingAppOpenAt = null
  showInstallFallback.value = true
  visible.value = true
}

function assumeAppOpened() {
  // Hide only for this page view — do NOT persist dismiss (open ≠ dismiss)
  opening.value = false
  deepLinkAttemptActive = false
  leftDuringAttempt = false
  showInstallFallback.value = false
  visible.value = false
  hidAssumingAppOpenAt = Date.now()
}

function onVisibilityChange() {
  if (typeof document === 'undefined') return

  if (deepLinkAttemptActive) {
    if (document.hidden) {
      // May have switched to the app — do not dismiss yet; wait for timeout / return
      leftDuringAttempt = true
      return
    }

    // Returned to the browser while attempting open → treat as failure
    if (leftDuringAttempt) {
      clearDeepLinkTimer()
      revealInstallFallback()
    }
    return
  }

  // Came back shortly after we hid assuming success → restore + offer install
  if (
    !document.hidden &&
    hidAssumingAppOpenAt != null &&
    Date.now() - hidAssumingAppOpenAt < RETURN_GRACE_MS
  ) {
    revealInstallFallback()
  }
}

function onPageHide() {
  if (deepLinkAttemptActive) {
    leftDuringAttempt = true
  }
}

function openInApp() {
  if (opening.value) return
  opening.value = true
  showInstallFallback.value = false
  deepLinkAttemptActive = true
  leftDuringAttempt = false
  hidAssumingAppOpenAt = null
  clearDeepLinkTimer()

  tryOpenNativeApp()

  deepLinkTimer = setTimeout(() => {
    deepLinkTimer = null
    deepLinkAttemptActive = false

    // Still in the browser → offer install; keep banner visible
    if (!document.hidden) {
      revealInstallFallback()
      return
    }

    // Page still hidden after timeout and we observed a leave → likely opened the app
    if (leftDuringAttempt) {
      assumeAppOpened()
      return
    }

    // Hidden for other reasons without a leave signal — keep banner, show install
    revealInstallFallback()
  }, DEEP_LINK_TIMEOUT_MS)
}

function downloadOrStore() {
  if (!installUrl.value) return
  window.open(installUrl.value, '_blank', 'noopener,noreferrer')
}

watch(visible, (on) => setBodyPad(on), { immediate: true })

onMounted(async () => {
  if (!isMobileBrowser.value || isDismissed() || wasShownThisSession()) return
  visible.value = true
  markShownThisSession()
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', onPageHide)
  await loadInstallInfo()
})

onUnmounted(() => {
  clearDeepLinkTimer()
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('pagehide', onPageHide)
  setBodyPad(false)
})
</script>

<template>
  <div
    v-if="visible"
    class="smart-banner fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4 pointer-events-none"
    role="dialog"
    :aria-label="t('smart_banner.title')"
  >
    <div
      class="pointer-events-auto mx-auto max-w-lg rounded-xl border border-primary/20 bg-surface-light dark:bg-surface-dark shadow-lg shadow-primary/10 overflow-hidden"
      :dir="locale === 'ar' ? 'rtl' : 'ltr'"
    >
      <div class="flex items-start gap-3 p-3 sm:p-4">
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-white font-display text-lg font-bold"
          aria-hidden="true"
        >
          ذ
        </div>
        <div class="min-w-0 flex-1">
          <p class="font-display text-base font-bold text-text-primary-light dark:text-text-primary-dark leading-snug">
            {{ t('smart_banner.title') }}
          </p>
          <p class="mt-0.5 text-sm text-text-secondary-light dark:text-text-secondary-dark">
            {{ showInstallFallback ? t('smart_banner.not_installed') : t('smart_banner.subtitle') }}
          </p>
          <p v-if="apkVersion && showInstallFallback && !installIsStore" class="mt-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
            {{ t('smart_banner.version', { version: apkVersion }) }}
          </p>
        </div>
        <button
          type="button"
          class="shrink-0 rounded-lg p-1.5 text-text-secondary-light dark:text-text-secondary-dark hover:bg-gray-100 dark:hover:bg-gray-800"
          :aria-label="t('smart_banner.dismiss')"
          @click="dismiss"
        >
          <span class="material-symbols-outlined text-xl">close</span>
        </button>
      </div>

      <div class="flex flex-col gap-2 border-t border-gray-100 dark:border-gray-800 px-3 py-3 sm:px-4">
        <button
          type="button"
          class="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-60"
          :disabled="opening"
          @click="openInApp"
        >
          {{ opening ? t('smart_banner.opening') : t('smart_banner.open_app') }}
        </button>
        <button
          v-if="showInstallFallback && installUrl"
          type="button"
          class="w-full rounded-lg border border-primary/40 bg-transparent px-4 py-2.5 text-sm font-semibold text-primary hover:bg-primary/5"
          @click="downloadOrStore"
        >
          {{ installIsStore ? t('smart_banner.get_on_play') : t('smart_banner.install_apk') }}
        </button>
        <button
          type="button"
          class="w-full py-1 text-xs text-text-secondary-light dark:text-text-secondary-dark hover:underline"
          @click="dismiss"
        >
          {{ t('smart_banner.continue_web') }}
        </button>
      </div>
    </div>
  </div>
</template>
